import { useState, useMemo } from 'react'
import { useAuth } from '../context/AuthContext'
import { trackLead } from '../utils/metaPixel'
import merlinImg from '../assets/merlin.png'

// Badge places restantes béta — affiche X/Y places, ou label custom si set
// côté admin. Info pullée au mount via AuthContext (betaInfo).
function BetaSpotsBadge() {
  const { betaInfo, betaGateEnabled } = useAuth()
  if (!betaGateEnabled || !betaInfo) return null
  const { max_spots, taken_spots, remaining, label, is_full } = betaInfo
  if (!max_spots || max_spots <= 0) return null
  // Label custom prime (ex: "Phase 2 fermée")
  if (label) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(254,130,4,0.1)', border: '1px solid rgba(254,130,4,0.25)' }}>
        <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: '#fdba74' }}>{label}</span>
      </div>
    )
  }
  // Full : liste d'attente
  if (is_full) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}>
        <span className="text-[10px]">🔴</span>
        <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: '#fca5a5' }}>Béta complète · liste d'attente</span>
      </div>
    )
  }
  // Normal : X places restantes sur Y
  const pctTaken = Math.min(100, Math.round((taken_spots / max_spots) * 100))
  const urgent = remaining <= Math.max(5, Math.ceil(max_spots * 0.2)) // <20% ou <=5 → urgent
  return (
    <div className="mt-3 inline-flex flex-col items-center gap-1.5">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: urgent ? 'rgba(254,130,4,0.12)' : 'rgba(124,58,237,0.1)', border: `1px solid ${urgent ? 'rgba(254,130,4,0.3)' : 'rgba(124,58,237,0.25)'}` }}>
        <span className="text-[10px]">{urgent ? '⚡' : '🧙'}</span>
        <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: urgent ? '#fdba74' : '#c4b5fd' }}>
          {remaining} place{remaining > 1 ? 's' : ''} restante{remaining > 1 ? 's' : ''} sur {max_spots}
        </span>
      </div>
      <div className="w-44 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full transition-all" style={{ width: `${pctTaken}%`, background: urgent ? 'linear-gradient(90deg, #fe8204, #f59e0b)' : 'linear-gradient(90deg, #7c3aed, #a78bfa)' }} />
      </div>
    </div>
  )
}

export default function LoginPage() {
  const { signInWithEmail, signUpWithEmail, signInWithGoogle } = useAuth()
  const [mode, setMode] = useState('login') // 'login' | 'signup'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [consent, setConsent] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [confirmEmail, setConfirmEmail] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (mode === 'signup' && !consent) {
      setError('Merci de cocher la case pour continuer.')
      return
    }

    setLoading(true)

    try {
      if (mode === 'signup') {
        const result = await signUpWithEmail(email, password, name, { consent })
        trackLead({ method: 'email' })
        if (result.user && !result.session) {
          setConfirmEmail(true) // Supabase requires email confirmation
        }
      } else {
        await signInWithEmail(email, password)
      }
    } catch (err) {
      // Email pas encore confirmé → bascule sur l'écran de confirmation
      // pour proposer de renvoyer l'email + voir le rappel spam.
      if (err.message?.toLowerCase().includes('email not confirmed')
          || err.message?.toLowerCase().includes('not confirmed')) {
        setConfirmEmail(true)
        setError('')
      } else {
        setError(err.message === 'Invalid login credentials'
          ? 'Email ou mot de passe incorrect'
          : err.message === 'User already registered'
          ? 'Cet email est deja utilise'
          : err.message)
      }
    }
    setLoading(false)
  }

  async function handleGoogle() {
    setError('')
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err.message)
    }
  }

  // Stable sparkle positions
  const sparklePositions = useMemo(() =>
    [...Array(12)].map(() => ({
      left: `${5 + Math.random() * 90}%`,
      top: `${10 + Math.random() * 80}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${2 + Math.random() * 2.5}s`,
    })), []);

  if (confirmEmail) {
    return (
      <div className="min-h-screen onboarding-bg flex items-center justify-center p-4">
        <div className="w-full max-w-sm merlin-welcome-bubble p-8 text-center">
          <div className="text-4xl mb-4">📧</div>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'rgba(255,255,255,0.9)' }}>Verifie ton email</h2>
          <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
            On t'a envoye un lien de confirmation a <strong style={{ color: '#fbbf24' }}>{email}</strong>. Clique dessus pour activer ton compte.
          </p>
          {/* Bouton 'Renvoyer l'email' — utile si Supabase rate-limit ou si
              l'user a perdu le mail. Resend Supabase via auth.resend(). */}
          <button
            onClick={async () => {
              setError('')
              setResending(true)
              try {
                const { sb } = await import('../utils/config')
                const { error: resendErr } = await sb.auth.resend({ type: 'signup', email })
                if (resendErr) throw resendErr
                setResendSuccess(true)
                setTimeout(() => setResendSuccess(false), 5000)
              } catch (e) {
                setError(e.message || 'Erreur lors du renvoi')
              }
              setResending(false)
            }}
            disabled={resending || resendSuccess}
            className="w-full mb-3 text-xs font-bold px-4 py-2.5 rounded-xl transition-all hover:scale-[1.02] disabled:opacity-50"
            style={{
              background: resendSuccess ? 'rgba(34,197,94,0.2)' : 'linear-gradient(135deg, #fe8204, #f59e0b)',
              color: 'white',
              boxShadow: resendSuccess ? 'none' : '0 2px 8px rgba(254,130,4,0.3)',
            }}
          >
            {resending ? 'Envoi...' : resendSuccess ? '✓ Email renvoyé' : '📧 Renvoyer l\'email de confirmation'}
          </button>
          {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
          <p className="text-[10px] mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Pense à vérifier les <b>spams</b> et les <b>onglets promotions/notifications</b> Gmail.
          </p>
          <button onClick={() => { setConfirmEmail(false); setMode('login'); setError('') }}
            className="text-xs hover:underline" style={{ color: '#fe8204' }}>
            Retour a la connexion
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen onboarding-bg flex items-center justify-center p-4 overflow-hidden relative">
      {/* Sparkles */}
      <div className="sparkles-container">
        {sparklePositions.map((pos, i) => (
          <div key={i} className="sparkle" style={pos} />
        ))}
      </div>

      {/* Ambient orbs */}
      <div className="welcome-orb welcome-orb-1" />
      <div className="welcome-orb welcome-orb-2" />

      <div className="w-full max-w-sm relative z-10 welcome-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="merlin-aura merlin-float inline-block mb-3">
            <img src={merlinImg} alt="Merlin" className="w-28 h-28 object-contain relative z-10"
              style={{ filter: 'drop-shadow(0 0 30px rgba(254,130,4,0.35)) brightness(1.1)' }} />
          </div>
          <h1 className="merlin-title text-3xl font-extrabold tracking-tight">Merlin</h1>
          <p className="text-xs mt-1.5 tracking-widest uppercase" style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.15em' }}>Contenu viral sur-mesure</p>

          {/* Badge béta : places restantes, affiché uniquement si info dispo */}
          <BetaSpotsBadge />
        </div>

        {/* Card — glass morphism */}
        <div className="merlin-welcome-bubble p-6 welcome-bubble-enter">
          <h2 className="text-sm font-bold mb-4" style={{ color: 'rgba(255,255,255,0.9)' }}>
            {mode === 'login' ? 'Connexion' : 'Creer un compte'}
          </h2>

          {/* Google OAuth */}
          <button onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-medium transition-all mb-4"
            style={{ border: '1.5px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.05)' }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </button>

          {/* Separator */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>ou</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
          </div>

          {/* Email form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'signup' && (
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Ton prenom"
                className="merlin-input-welcome w-full px-3 py-2.5 text-xs outline-none"
              />
            )}
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="merlin-input-welcome w-full px-3 py-2.5 text-xs outline-none"
            />
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Mot de passe"
              required
              minLength={6}
              className="merlin-input-welcome w-full px-3 py-2.5 text-xs outline-none"
            />

            {mode === 'signup' && (
              <label className="flex items-start gap-2.5 text-xs leading-snug cursor-pointer select-none py-1"
                style={{ color: 'rgba(255,255,255,0.55)' }}>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 accent-orange cursor-pointer"
                />
                <span>
                  J'accepte que Merlin utilise mes données (profil, vidéos analysées, scripts,
                  réponses aux questions) pour <strong style={{ color: 'rgba(255,255,255,0.75)' }}>personnaliser mon expérience</strong>
                  {' '}et <strong style={{ color: 'rgba(255,255,255,0.75)' }}>améliorer le produit</strong> via des analyses anonymisées.
                </span>
              </label>
            )}

            {error && (
              <p className="text-xs rounded-lg px-3 py-2" style={{ color: '#fca5a5', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>{error}</p>
            )}

            <button type="submit" disabled={loading || (mode === 'signup' && !consent)}
              className="merlin-send-btn w-full py-2.5 text-xs font-bold disabled:opacity-50">
              {loading ? '...' : mode === 'login' ? 'Se connecter' : 'Creer mon compte'}
            </button>
          </form>

          {/* Toggle mode */}
          <p className="text-center text-xs mt-4" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {mode === 'login' ? (
              <>Pas encore de compte ? <button onClick={() => { setMode('signup'); setError('') }} className="hover:underline font-medium" style={{ color: '#fe8204' }}>S'inscrire</button></>
            ) : (
              <>Deja un compte ? <button onClick={() => { setMode('login'); setError('') }} className="hover:underline font-medium" style={{ color: '#fe8204' }}>Se connecter</button></>
            )}
          </p>
        </div>

        {/* Plans preview — beta pricing (crossed-out regular price above) */}
        <div className="mt-6 space-y-2 welcome-input-enter">
          <p className="text-[10px] uppercase tracking-widest text-center" style={{ color: 'rgba(254,130,4,0.7)' }}>
            ★ Tarifs beta — figés tant que tu restes abonné
          </p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { name: 'Free', scripts: '600 credits offerts', price: '0€', regular: null },
              { name: 'Basic', scripts: '3 000 credits/mois', price: '39€', regular: '69€' },
              { name: 'Createur', scripts: '10 000 credits/mois', price: '79€', regular: '149€' },
              { name: 'Pro', scripts: '50 000 credits/mois', price: '249€', regular: '499€' },
            ].map(p => (
              <div key={p.name} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>{p.name}</p>
                <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{p.scripts}</p>
                {p.regular && (
                  <p className="text-[9px] line-through mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{p.regular}</p>
                )}
                <p className="text-xs font-bold" style={{ color: '#fe8204' }}>{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
